import React, { PropTypes as pt } from 'react';
import moment from 'moment-timezone';
import DateTimeComponent from '../../dateTime/index';

/**
 * Date+time input component with native calendar/clock controls.
 */
class DateTimePicker extends React.Component {
    static propTypes = {
        value: pt.any,
        initialDate: pt.string,
        initialTime: pt.string,
        disabled: pt.bool,
        onChange: pt.func.isRequired,
        error: pt.oneOfType([pt.string, pt.array]),
        onCETFormattedChange: pt.func,
        previewTimezone: pt.string,
    };

    static getInitialDisplay(value, timezoneName) {
        if (!value) return { date: '', time: '' };
        const zone = timezoneName || 'Europe/Moscow';
        const m = moment(value).tz(zone);
        if (!m.isValid()) return { date: '', time: '' };
        return {
            date: m.format('YYYY-MM-DD'),
            time: m.format('HH:mm'),
        };
    }

    static formatLocal(value, timezoneName) {
        if (!value) return '';
        try {
            const zone = timezoneName || 'Europe/Moscow';
            const m = moment(value).tz(zone);
            if (!m.isValid()) return '';
            return m.locale('en').format('dddd, MMM D, HH:mm') + ' ' + zone;
        } catch (e) {
            return '';
        }
    }

    constructor(props) {
        super(props);
        const d = DateTimePicker.getInitialDisplay(props.value, props.previewTimezone);
        const hasValue = props.value != null && String(props.value).trim() !== '';
        const useServerHints = !hasValue && props.initialDate && props.initialTime;
        this.state = {
            date: useServerHints ? props.initialDate : d.date,
            time: useServerHints ? props.initialTime : d.time,
            localFormatted: '',
        };
        this.dateInputRef = null;
        this.timeInputRef = null;
    }

    componentDidMount() {
        this.initLegacyPickers();
        this.syncPickerInputs(this.state.date, this.state.time);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            const d = DateTimePicker.getInitialDisplay(this.props.value, this.props.previewTimezone);
            const nextState = {
                date: d.date,
                time: d.time,
                localFormatted: DateTimePicker.formatLocal(this.props.value, this.props.previewTimezone),
            };

            if (
                this.state.date !== nextState.date ||
                this.state.time !== nextState.time ||
                this.state.localFormatted !== nextState.localFormatted
            ) {
                this.setState(nextState);
                this.syncPickerInputs(nextState.date, nextState.time);
            }
        }
        if (prevProps.previewTimezone !== this.props.previewTimezone) {
            if (this.props.value) {
                const d = DateTimePicker.getInitialDisplay(this.props.value, this.props.previewTimezone);
                const localFormatted = DateTimePicker.formatLocal(this.props.value, this.props.previewTimezone);
                if (
                    this.state.date !== d.date ||
                    this.state.time !== d.time ||
                    this.state.localFormatted !== localFormatted
                ) {
                    this.setState({ date: d.date, time: d.time, localFormatted }, () => {
                        this.syncPickerInputs(d.date, d.time);
                        if (this.props.onCETFormattedChange) {
                            this.props.onCETFormattedChange(localFormatted);
                        }
                    });
                }
            } else if (this.props.onCETFormattedChange) {
                this.props.onCETFormattedChange('');
            }
        }
    }

    componentWillUnmount() {
        this.destroyLegacyPickers();
    }

    initLegacyPickers = () => {
        const jq = (typeof window !== 'undefined') && (window.jQuery || window.$);
        if (!jq) {
            return;
        }

        if (this.dateInputRef && jq.fn && jq.fn.datepicker) {
            const dateEl = jq(this.dateInputRef);
            dateEl.datepicker({
                format: 'yyyy-mm-dd',
                autoclose: true,
                todayHighlight: true,
                orientation: 'bottom auto',
            });
            dateEl.on('changeDate change', () => {
                this.handleDateChange({ target: { value: dateEl.val() } });
            });
        }

        if (this.timeInputRef && jq.fn && jq.fn.clockpicker) {
            const timeEl = jq(this.timeInputRef);
            timeEl.clockpicker({
                autoclose: true,
                placement: 'bottom',
                align: 'left',
                donetext: 'Done',
                twelvehour: false,
                afterDone: () => {
                    this.handleTimeChange({ target: { value: timeEl.val() } });
                },
            });
            timeEl.on('change', () => {
                this.handleTimeChange({ target: { value: timeEl.val() } });
            });
        }
    };

    destroyLegacyPickers = () => {
        const jq = (typeof window !== 'undefined') && (window.jQuery || window.$);
        if (!jq || !jq.fn) {
            return;
        }
        if (this.dateInputRef && jq.fn.datepicker) {
            try {
                jq(this.dateInputRef).datepicker('destroy');
            } catch (e) {}
        }
        if (this.timeInputRef && jq.fn.clockpicker) {
            try {
                jq(this.timeInputRef).clockpicker('remove');
            } catch (e) {}
        }
    };

    syncPickerInputs = (date, time) => {
        const jq = (typeof window !== 'undefined') && (window.jQuery || window.$);
        if (!jq || !jq.fn) {
            return;
        }
        if (this.dateInputRef) {
            const dateEl = jq(this.dateInputRef);
            dateEl.val(date || '');
            if (jq.fn.datepicker) {
                try {
                    dateEl.datepicker('update', date || '');
                } catch (e) {}
            }
        }
        if (this.timeInputRef) {
            jq(this.timeInputRef).val(time || '');
        }
    };

    handleDateChange = (e) => {
        const date = e.target.value;
        this.setState({ date }, () => {
            this.notifyChange(date, this.state.time);
        });
    };

    handleTimeChange = (e) => {
        const time = e.target.value;
        this.setState({ time }, () => {
            this.notifyChange(this.state.date, time);
        });
    };

    notifyChange = (date, time) => {
        try {
            let isoValue = '';
            const timezone = this.props.previewTimezone || 'Europe/Moscow';

            if (date && time) {
                isoValue = DateTimeComponent.fromDateTimeStrings(date, time, timezone).toISO();
            }

            if (isoValue) {
                const localFormatted = DateTimePicker.formatLocal(isoValue, this.props.previewTimezone);
                this.setState({ localFormatted });
                if (this.props.onCETFormattedChange) {
                    this.props.onCETFormattedChange(localFormatted);
                }
                this.props.onChange(isoValue);
            } else {
                this.props.onChange('');
                if (this.props.onCETFormattedChange) {
                    this.props.onCETFormattedChange('');
                }
                this.setState({ localFormatted: '' });
            }
        } catch (e) {
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('DateTimePicker onChange error:', e);
            }
        }
    };

    render() {
        const { disabled, error } = this.props;
        const formattedError = Array.isArray(error) ? error[0] : error;
        const hasError = !!formattedError;
        const errorClass = hasError ? 'has-error has-danger' : '';
        const inputErrorClass = hasError ? 'is-invalid' : '';

        return (
            <div className={errorClass}>
                <style>{`
                    .lesson-datetime-picker {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 10px;
                    }
                    .lesson-datetime-picker .field {
                        flex: 1 1 200px;
                    }
                    .lesson-datetime-picker .field .input-group-addon {
                        min-width: 36px;
                        text-align: center;
                    }
                    .lesson-datetime-picker .field input.form-control {
                        height: 34px;
                    }
                    .lesson-datetime-picker input.${inputErrorClass} {
                        border-color: #a94442;
                    }
                    .lesson-datetime-picker input:disabled {
                        background-color: #eee;
                        cursor: not-allowed;
                    }
                    .lesson-datetime-help {
                        margin-top: 6px;
                    }
                `}</style>
                <div className="lesson-datetime-picker">
                    <div className="field input-group">
                        <span className="input-group-addon">
                            <i className="fa fa-calendar" />
                        </span>
                        <input
                            type="text"
                            className={`form-control ${inputErrorClass}`}
                            value={this.state.date}
                            onChange={this.handleDateChange}
                            disabled={disabled}
                            id="lsf-date-input"
                            placeholder="YYYY-MM-DD"
                            ref={(el) => { this.dateInputRef = el; }}
                        />
                    </div>
                    <div className="field input-group">
                        <span className="input-group-addon">
                            <i className="fa fa-clock-o" />
                        </span>
                        <input
                            type="text"
                            className={`form-control ${inputErrorClass}`}
                            value={this.state.time}
                            onChange={this.handleTimeChange}
                            disabled={disabled}
                            id="lsf-time-input"
                            placeholder="HH:mm"
                            ref={(el) => { this.timeInputRef = el; }}
                        />
                    </div>
                </div>
                <small className="help-block text-muted lesson-datetime-help">
                    <i className="fa fa-info-circle" /> Choose date and time in your local timezone ({this.props.previewTimezone || 'Europe/Moscow'}).
                </small>
                {formattedError && (
                    <small className="help-block text-danger">
                        {formattedError}
                    </small>
                )}
            </div>
        );
    }
}

export default DateTimePicker;



// WEBPACK FOOTER //
// ./src/incab/common/components/form/DateTimePicker.jsx